import * as React from 'react';
import ReactQuill from 'react-quill';

import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Body} from 'shells/typography/body';
import {useDebounce} from 'shells/utils/hooks/use-debounce';

import {TextareaMentionable} from '../../../core/textarea-mentionable/textarea-mentionable';
import {NOTES_PLACEHOLDER} from '../../../phone-call/phone-call-view';

import styles from './activity-log-modal.module.css';

type Props = {
    onLogActivity: () => void;
    setNote: (noteText: string, noteHtml: string) => void;
    onClose: () => void;
    initialValue?: string;
    initialTextValue?: string;
};

export function NotesSection({setNote, ...props}: Props) {
    const quillRef = React.useRef<ReactQuill | null | undefined>(null);
    const [htmlValue, setHtmlValue] = React.useState<string>(props.initialValue || '');
    const [textValue, setTextValue] = React.useState<string>(props.initialTextValue || '');
    const debouncedTextValue = useDebounce(textValue, 500);
    const debouncedHtmlValue = useDebounce(htmlValue, 500);

    // Effect to position cursor at the end of the text on initial load
    React.useEffect(() => {
        if (quillRef.current && htmlValue) {
            const editor = quillRef.current.getEditor();
            // Set timeout to ensure editor is fully initialized
            setTimeout(() => {
                // Move cursor to the end of the content
                const length = editor.getLength();
                editor.setSelection(length, 0);
            }, 50);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Effect to call setNote when the debounced values change
    React.useEffect(() => {
        setNote(debouncedTextValue, debouncedHtmlValue);
    }, [debouncedTextValue, debouncedHtmlValue, setNote]);

    // @ts-expect-error
    const handleChange = (editorHtmlValue, htmlTextValue) => {
        setHtmlValue(editorHtmlValue);
        setTextValue(htmlTextValue);
    };

    return (
        <StackLegacy spacing={8}>
            <Body size='small' isBold={true}>
                Log a note
            </Body>
            <div className={styles['note-textarea-container']}>
                <TextareaMentionable
                    id='activityLogEditor'
                    getQuillRef={(ref: HTMLElement | null | undefined) => {
                        quillRef.current = ref as ReactQuill | null | undefined;
                    }}
                    placeholder={NOTES_PLACEHOLDER}
                    value={htmlValue}
                    onChange={handleChange}
                    onTabPropagateToQuill={true}
                    autoFocus={true}
                    overflowVisible={true}
                    noPadding={true}
                    noPlaceholderSpacing={true}
                    isFullHeight={true}
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === 'Escape') {
                            props.onClose();
                        }
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                            props.onLogActivity();
                        }
                    }}
                />
            </div>
        </StackLegacy>
    );
}
